import { motion } from "framer-motion";
import { Check, ArrowUpRight, KeyRound } from "lucide-react";
import { useLanguage, waLink } from "../i18n";

const EASE = [0.22, 1, 0.36, 1];

const Owners = () => {
  const { t } = useLanguage();
  return (
    <section
      id="propietarios"
      className="bg-[#F2E8D5]/50 py-24 sm:py-32"
      data-testid="owners-section"
    >
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <div className="mb-14 grid grid-cols-1 gap-6 lg:grid-cols-12 lg:items-end">
          <div className="lg:col-span-8">
            <motion.p
              initial={{ opacity: 0, y: 14 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-80px" }}
              transition={{ duration: 0.6, ease: EASE }}
              className="mb-4 text-xs font-semibold uppercase tracking-[0.25em] text-[#C85A32]"
            >
              {t.owners.eyebrow}
            </motion.p>
            <motion.h2
              initial={{ opacity: 0, y: 24 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-80px" }}
              transition={{ duration: 0.7, delay: 0.08, ease: EASE }}
              className="max-w-2xl font-serif text-4xl font-black tracking-tight text-[#0D1B2A] sm:text-5xl"
            >
              {t.owners.title}
            </motion.h2>
            <motion.p
              initial={{ opacity: 0, y: 18 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-80px" }}
              transition={{ duration: 0.7, delay: 0.16, ease: EASE }}
              className="mt-5 max-w-xl text-base leading-relaxed text-[#0D1B2A]/60"
            >
              {t.owners.sub}
            </motion.p>
          </div>
          <motion.p
            initial={{ opacity: 0, rotate: -5 }}
            whileInView={{ opacity: 1, rotate: -2 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8, delay: 0.2, ease: EASE }}
            className="font-script text-2xl leading-tight text-[#1B6B93] lg:col-span-4"
          >
            {t.owners.note}
          </motion.p>
        </div>

        <div className="grid grid-cols-1 gap-6 md:grid-cols-3">
          {t.owners.packs.map((pack, i) => {
            const featured = Boolean(pack.featured);
            return (
              <motion.article
                key={pack.name}
                data-testid={`owner-pack-${i + 1}`}
                initial={{ opacity: 0, y: 32 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-60px" }}
                transition={{ duration: 0.6, delay: i * 0.1, ease: EASE }}
                className={`relative flex flex-col rounded-3xl p-8 transition-all duration-300 hover:-translate-y-1.5 ${
                  featured
                    ? "bg-[#0D1B2A] text-[#FAF6F0] shadow-2xl shadow-[#0D1B2A]/30 md:-translate-y-3 md:hover:-translate-y-4"
                    : "border border-[#E6DEC8] bg-white hover:border-[#C85A32]/50 hover:shadow-2xl hover:shadow-[#C85A32]/10"
                }`}
              >
                {featured && (
                  <span
                    data-testid="owner-pack-featured-badge"
                    className="absolute -top-3.5 left-1/2 -translate-x-1/2 whitespace-nowrap rounded-full bg-[#C85A32] px-4 py-1.5 text-xs font-bold uppercase tracking-widest text-white shadow-lg"
                  >
                    {t.owners.badge}
                  </span>
                )}
                <p
                  className={`text-xs font-semibold uppercase tracking-[0.2em] ${
                    featured ? "text-[#F2A33C]" : "text-[#C85A32]"
                  }`}
                >
                  {pack.tagline}
                </p>
                <h3 className="mt-2 font-serif text-2xl font-bold sm:text-3xl">
                  {pack.name}
                </h3>
                <ul className="mt-6 flex-1 space-y-3">
                  {pack.items.map((item) => (
                    <li key={item} className="flex items-start gap-2.5 text-sm leading-relaxed">
                      <span
                        className={`mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-full ${
                          featured ? "bg-[#25D366] text-white" : "bg-[#1B6B93] text-white"
                        }`}
                      >
                        <Check size={12} strokeWidth={3} />
                      </span>
                      <span className={featured ? "text-[#FAF6F0]/80" : "text-[#0D1B2A]/70"}>
                        {item}
                      </span>
                    </li>
                  ))}
                </ul>
                <a
                  data-testid={`owner-pack-cta-${i + 1}`}
                  href={waLink(pack.wa)}
                  target="_blank"
                  rel="noopener noreferrer"
                  className={`group mt-8 inline-flex items-center justify-center gap-2 rounded-full px-6 py-3.5 text-sm font-bold transition-all duration-300 hover:scale-[1.03] ${
                    featured
                      ? "bg-[#25D366] text-white shadow-lg shadow-[#25D366]/30"
                      : "border-2 border-[#0D1B2A] text-[#0D1B2A] hover:bg-[#0D1B2A] hover:text-[#FAF6F0]"
                  }`}
                >
                  {t.owners.cta}
                  <ArrowUpRight size={16} className="transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
                </a>
              </motion.article>
            );
          })}
        </div>

        <motion.p
          initial={{ opacity: 0, y: 18 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.7, delay: 0.2, ease: EASE }}
          className="mx-auto mt-12 flex max-w-lg items-center justify-center gap-3 text-center text-sm leading-relaxed text-[#0D1B2A]/60"
        >
          <KeyRound size={18} className="shrink-0 text-[#C85A32]" />
          {t.owners.keys}
        </motion.p>
      </div>
    </section>
  );
};

export default Owners;
